import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { id: "address-list" }
const _hoisted_3 = ["id"]
const _hoisted_4 = { class: "form__duo-fields" }
const _hoisted_5 = { class: "form__submit btn btn--fullscreen" }

import {computed, ref} from "vue"
import {useI18n} from "vue3-i18n"
import {notifyError} from "@/notify";
import {useMemberStore} from "@/stores/memberStore";
import {useAddressService} from "@/inversify.config"
import {min, mustMatchPhoneNumberFormat, mustMatchZipCodeFormat, required} from "@/validation/rules";
import {BrokerTypeHelper, FormOption, OrganizationType, Role} from "@/types";
import {IFullOrganization} from "@/types/entities/organization";
import {Status} from "@/validation";
import FormRow from "@/components/forms/FormRow.vue";
import FormInput from "@/components/forms/FormInput.vue"
import FormReadonlyField from "@/components/forms/FormReadonlyField.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import {IAddress} from "@/types/responses";
import {IFilterAddressesRequest, IGetAddressRequest} from "@/types/requests";
import FormMultiSelect from "@/components/forms/FormMultiSelect.vue";
import {ActivityAreaHelper} from "@/types/activityArea";
import FormTextEditor from "@/components/forms/FormTextEditor.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import {getReferralSourceLabel, isLegacyReferralSource} from "@/types/entities/organization";

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'OrganizationForm',
  props: {
    modelValue: {}
  },
  emits: ["update:modelValue", "onImageChange", "submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props

// eslint-disable-next-line
const emit = __emit;

const {t, getLocale} = useI18n()
const memberStore = useMemberStore()
const addressService = useAddressService()

const addressOptions = ref<IAddress[]>([])
const activityAreas = ref<FormOption[]>(props.modelValue.activityAreas
    ? ActivityAreaHelper.convertActivityAreaKeysToFormOptions(props.modelValue.activityAreas)
    : [])
const organization = ref<IFullOrganization>(props.modelValue)

let organizationType = computed(() => {
  if (!organization.value)
    return;
  let type = organization.value.type.makeFirstLetterLowercase();
  return t(`organization.types.${type}`)
})

const referralSourceLabel = computed(() => getReferralSourceLabel(organization.value?.referralSource))
const showReferralSource = computed(() => {
  const source = organization.value?.referralSource
  if (!source) return false
  if (isLegacyReferralSource(source)) return memberStore.hasRole(Role.Admin)
  return true
})

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses: any = {}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref))
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function onImageChange(event: any) {
  const files = event?.target?.files
  if (!files)
    return;
  emit('onImageChange', files.length === 0 ? undefined : files[0])
}

function onActivityAreaChange(newOptions: FormOption[]) {
  activityAreas.value = newOptions
  organization.value.activityAreas = activityAreas.value.map(x => x.name)
}

async function handleInput(inputValue: string) {
  let selectedOptions = addressOptions.value.filter(x => x.text == inputValue)
  if (selectedOptions.length > 0) {
    let request = {
      id: selectedOptions[0].id,
      language: getLocale()
    } as IGetAddressRequest
    let response = await addressService.getAddress(request)
    organization.value.street = response.detailedAddress.street
    organization.value.city = response.detailedAddress.city
    organization.value.zipCode = response.detailedAddress.zipCode.replace(' ', '')
    return;
  }
  await loadMatchingAddresses(inputValue)
}

async function loadMatchingAddresses(inputValue: string) {
  let request = {
    street: inputValue,
    language: getLocale()
  } as IFilterAddressesRequest
  let addresses = await addressService.filterAddresses(request)
  addressOptions.value = addresses.matchingAddresses
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  }
  emit('submit', organization.value as IFullOrganization)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "form",
    novalidate: "",
    onSubmit: _withModifiers(handleSubmit, ["prevent"])
  }, [
    (organization.value.type == _unref(OrganizationType).Corporate)
      ? (_openBlock(), _createBlock(FormRow, { key: 0 }, {
          default: _withCtx(() => [
            _createVNode(FormFiles, {
              modelValue: organization.value.logo,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event: any) => ((organization.value.logo) = $event)),
                _cache[2] || (_cache[2] = ($event: any) => (onImageChange($event)))
              ],
              "default-selected-files": organization.value.logo != null ? [organization.value.logo] : [],
              label: _unref(t)('organization.logo'),
              possiblesTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
              tooltip: _unref(t)(`organization.logoFormat`),
              name: "logo",
              onChange: _cache[1] || (_cache[1] = ($event: any) => (onImageChange($event)))
            }, null, 8, ["modelValue", "default-selected-files", "label", "tooltip"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(memberStore).hasRole(_unref(Role).Admin))
      ? (_openBlock(), _createBlock(FormRow, {
          key: 1,
          withThreeColumns: true
        }, {
          default: _withCtx(() => [
            _createVNode(FormReadonlyField, {
              modelValue: organization.value.crmId,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((organization.value.crmId) = $event)),
              label: _unref(t)('global.crmId'),
              name: "crmId"
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormReadonlyField, {
              modelValue: _unref(organizationType),
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(organizationType) ? (organizationType).value = $event : organizationType = $event)),
              label: _unref(t)('organization.type'),
              name: "type"
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormReadonlyField, {
              modelValue: organization.value.chapter,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((organization.value.chapter) = $event)),
              label: _unref(t)('organization.chapter'),
              name: "chapter"
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }))
      : (_openBlock(), _createBlock(FormRow, {
          key: 2,
          withThreeColumns: true
        }, {
          default: _withCtx(() => [
            _createVNode(FormReadonlyField, {
              modelValue: _unref(organizationType),
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(organizationType) ? (organizationType).value = $event : organizationType = $event)),
              label: _unref(t)('organization.type'),
              name: "type"
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormReadonlyField, {
              modelValue: organization.value.chapter,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((organization.value.chapter) = $event)),
              label: _unref(t)('organization.chapter'),
              name: "chapter"
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormReadonlyField, {
              modelValue: organization.value.memberNumber,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((organization.value.memberNumber) = $event)),
              label: _unref(t)('organization.memberNumber'),
              name: "memberNumber"
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        })),
    _createVNode(FormRow, { withThreeColumns: true }, {
      default: _withCtx(() => [
        _createVNode(FormReadonlyField, {
          modelValue: organization.value.subscription.startAt,
          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((organization.value.subscription.startAt) = $event)),
          label: _unref(t)('organization.subscriptionStartAt'),
          name: "subscriptionStartAt"
        }, null, 8, ["modelValue", "label"]),
        _createVNode(FormReadonlyField, {
          modelValue: organization.value.subscription.endAt,
          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((organization.value.subscription.endAt) = $event)),
          label: _unref(t)('organization.subscriptionEndAt'),
          name: "subscriptionEndAt"
        }, null, 8, ["modelValue", "label"]),
        _createVNode(FormInput, {
          ref: addFormInputRef,
          modelValue: organization.value.enterpriseNumber,
          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((organization.value.enterpriseNumber) = $event)),
          label: _unref(t)('organization.enterpriseNumber'),
          name: "enterpriseNumber",
          type: "text",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"])
      ]),
      _: 1
    }),
    _createVNode(FormRow, { withThreeColumns: true }, {
      default: _withCtx(() => [
        _createVNode(FormInput, {
          ref: addFormInputRef,
          modelValue: organization.value.apartment,
          "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((organization.value.apartment) = $event)),
          label: _unref(t)('global.apartment'),
          rules: [],
          name: "apartment",
          type: "text",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"]),
        _createVNode(FormInput, {
          ref: addFormInputRef,
          modelValue: organization.value.street,
          "onUpdate:modelValue": [
            _cache[13] || (_cache[13] = ($event: any) => ((organization.value.street) = $event)),
            handleInput
          ],
          label: _unref(t)('global.street'),
          list: 'address-list',
          name: "street",
          type: "text",
          onValidated: handleValidation
        }, {
          "before-input": _withCtx(() => [
            _createElementVNode("datalist", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(addressOptions.value, (address) => {
                return (_openBlock(), _createElementBlock("option", {
                  id: address.id,
                  key: address.id
                }, _toDisplayString(address.text), 9, _hoisted_3))
              }), 128))
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "label"]),
        _createVNode(FormInput, {
          ref: addFormInputRef,
          modelValue: organization.value.city,
          "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((organization.value.city) = $event)),
          label: _unref(t)('global.city'),
          name: "city",
          type: "text",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"])
      ]),
      _: 1
    }),
    _createVNode(FormRow, { withThreeColumns: true }, {
      default: _withCtx(() => [
        _createVNode(FormInput, {
          ref: addFormInputRef,
          modelValue: organization.value.zipCode,
          "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((organization.value.zipCode) = $event)),
          label: _unref(t)('global.zipCode'),
          rules: [_unref(required), _unref(mustMatchZipCodeFormat)],
          name: "zipCode",
          type: "zip",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label", "rules"]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(FormInput, {
            ref: addFormInputRef,
            modelValue: organization.value.phoneNumber,
            "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((organization.value.phoneNumber) = $event)),
            label: _unref(t)('global.phoneNumber'),
            rules: [_unref(required), _unref(mustMatchPhoneNumberFormat)],
            name: "phoneNumber",
            type: "tel",
            onValidated: handleValidation
          }, null, 8, ["modelValue", "label", "rules"]),
          _createVNode(FormInput, {
            ref: addFormInputRef,
            modelValue: organization.value.phoneExtension,
            "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((organization.value.phoneExtension) = $event)),
            label: _unref(t)('global.phoneExtension'),
            rules: [],
            name: "phoneExtension",
            type: "number",
            onValidated: handleValidation
          }, null, 8, ["modelValue", "label"])
        ]),
        _createVNode(FormInput, {
          ref: addFormInputRef,
          modelValue: organization.value.email,
          "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((organization.value.email) = $event)),
          label: _unref(t)('global.email'),
          name: "email",
          type: "text",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"])
      ]),
      _: 1
    }),
    _createVNode(FormRow, { withThreeColumns: true }, {
      default: _withCtx(() => [
        (organization.value.type === _unref(OrganizationType).Corporate)
          ? (_openBlock(), _createBlock(FormMultiSelect, {
              key: 0,
              ref: addFormInputRef,
              label: _unref(t)('organization.activityAreas'),
              "max-items": 5,
              modelValue: activityAreas.value,
              options: _unref(ActivityAreaHelper).activityAreaFormOptions(),
              name: "activityAreas",
              onOnOptionChange: onActivityAreaChange
            }, null, 8, ["label", "modelValue", "options"]))
          : _createCommentVNode("", true),
        (organization.value.type === _unref(OrganizationType).RealEstateBroker)
          ? (_openBlock(), _createBlock(FormSelect, {
              key: 1,
              modelValue: organization.value.brokerType,
              "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((organization.value.brokerType) = $event)),
              ref: addFormInputRef,
              label: _unref(t)('organization.brokerType'),
              options: _unref(BrokerTypeHelper).brokerTypeNameAndLabel() as FormOption[],
              name: "brokerType",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "options"]))
          : _createCommentVNode("", true),
        (_ctx.isSyndicateOrganization)
          ? (_openBlock(), _createBlock(FormSelect, {
              key: 2,
              ref: addFormInputRef,
              modelValue: _ctx.member.jobTitle,
              "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((_ctx.member.jobTitle) = $event)),
              label: _unref(t)('global.jobTitle'),
              options: _ctx.jobTitles,
              name: "jobTitle",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "options"]))
          : _createCommentVNode("", true),
        (organization.value.type === _unref(OrganizationType).Corporate)
          ? (_openBlock(), _createBlock(FormInput, {
              key: 3,
              ref: addFormInputRef,
              modelValue: organization.value.yearsOfExperience,
              "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((organization.value.yearsOfExperience) = $event)),
              label: _unref(t)('organization.yearsOfExperience'),
              name: "yearsOfExperience",
              type: "number",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]))
          : _createCommentVNode("", true),
        (organization.value.type === _unref(OrganizationType).Corporate || organization.value.type === _unref(OrganizationType).RealEstateBroker)
          ? (_openBlock(), _createBlock(FormInput, {
              key: 4,
              ref: addFormInputRef,
              modelValue: organization.value.website,
              "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((organization.value.website) = $event)),
              label: _unref(t)('organization.website'),
              rules: [],
              name: "website",
              type: "text",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]))
          : _createCommentVNode("", true),
        (organization.value.type === _unref(OrganizationType).RegularIndividual || organization.value.type === _unref(OrganizationType).RegularSyndicate)
          ? (_openBlock(), _createBlock(FormInput, {
              key: 5,
              ref: addFormInputRef,
              modelValue: organization.value.numberOfUnits,
              "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((organization.value.numberOfUnits) = $event)),
              label: _unref(t)('organization.numberOfUnits'),
              rules: [_unref(required), _unref(min)(2)],
              name: "numberOfUnits",
              type: "number",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "rules"]))
          : _createCommentVNode("", true),
        (organization.value.type === _unref(OrganizationType).RegularIndividual && organization.value.syndicate?.name)
          ? (_openBlock(), _createBlock(FormReadonlyField, {
              key: 6,
              modelValue: organization.value.syndicate.name,
              "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((organization.value.syndicate.name) = $event)),
              label: _unref(t)('organization.syndicate'),
              name: "syndicateName"
            }, null, 8, ["modelValue", "label"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (showReferralSource.value)
      ? (_openBlock(), _createBlock(FormRow, {
          key: 3,
          withThreeColumns: true
        }, {
          default: _withCtx(() => [
            _createVNode(FormReadonlyField, {
              modelValue: referralSourceLabel.value,
              label: _unref(t)('organization.referralSource'),
              name: "referralSource"
            }, null, 8, ["modelValue", "label"]),
            (organization.value.referralSourceDetails)
              ? (_openBlock(), _createBlock(FormReadonlyField, {
                  key: 0,
                  modelValue: organization.value.referralSourceDetails,
                  label: _unref(t)('organization.referralSourceDetails'),
                  name: "referralSourceDetails"
                }, null, 8, ["modelValue", "label"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (organization.value.type == _unref(OrganizationType).Corporate)
      ? (_openBlock(), _createBlock(FormTextEditor, {
          key: 4,
          ref: addFormInputRef,
          modelValue: organization.value.descriptionFr,
          "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((organization.value.descriptionFr) = $event)),
          label: _unref(t)('organization.descriptionFr'),
          rules: [],
          name: "description-fr",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"]))
      : _createCommentVNode("", true),
    (organization.value.type == _unref(OrganizationType).Corporate)
      ? (_openBlock(), _createBlock(FormTextEditor, {
          key: 5,
          ref: addFormInputRef,
          modelValue: organization.value.descriptionEn,
          "onUpdate:modelValue": _cache[26] || (_cache[26] = ($event: any) => ((organization.value.descriptionEn) = $event)),
          label: _unref(t)('organization.descriptionEn'),
          rules: [],
          name: "description-en",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"]))
      : _createCommentVNode("", true),
    _createElementVNode("button", _hoisted_5, _toDisplayString(_unref(t)('global.save')), 1)
  ], 40, _hoisted_1))
}
}

})